import { useEffect, useId, useState } from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import { DateTimePicker } from '@tribeplatform/react-ui-kit/DateTimePicker'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T, useI18n } from '../../i18n/index.js'

import { PollFormValues } from './types.js'

type OpensType = 'immediately' | 'schedule'

export function PollFormOpensAt({
  form,
}: {
  form: UseFormReturn<PollFormValues>
}) {
  const id = useId()
  const { $t } = useI18n()

  const [opensType, setOpensType] = useState<OpensType>(() => {
    const opensAt = form.getValues('opensAt')

    if (!opensAt) {
      return 'immediately'
    }

    if (opensAt > new Date()) {
      return 'schedule'
    }

    return 'immediately'
  })

  useEffect(() => {
    if (opensType === 'immediately') {
      const opensAt = form.getValues('opensAt')
      if (opensAt > new Date()) {
        form.setValue('opensAt', new Date(), { shouldDirty: true })
      } else {
        form.resetField('opensAt')
      }
    } else {
      form.resetField('opensAt')
    }
  }, [opensType, form])

  const maxDate = form.watch('closesAt') ?? undefined

  return (
    <div className="space-y-4">
      <FormControl.Label htmlFor={`poll-selection-type-${id}`}>
        <div className="flex flex-col">
          <Text variant="labelLg">
            <T
              id="Poll.Form.OpensAt.Title"
              defaultMessage="Voting start time"
            />
          </Text>
          <Text className="text-content-muted" variant="bodySm">
            <T
              id="Poll.Form.OpensAt.Description"
              defaultMessage="Choose when participants can begin voting"
            />
          </Text>
        </div>
      </FormControl.Label>
      <FormControl.RadioGroup
        id={`poll-selection-type-${id}`}
        name="opensType"
        value={opensType}
        onChange={value => {
          setOpensType(value as OpensType)
        }}
      >
        <RadioGroup.Items>
          <RadioGroup.Item
            value="immediately"
            title={$t({
              id: 'Poll.Form.OpensAt.Immediately',
              defaultMessage: 'Immediately',
            })}
          />
          <RadioGroup.Item
            value="schedule"
            title={$t({
              id: 'Poll.Form.OpensAt.Schedule',
              defaultMessage: 'Schedule for later',
            })}
          />
        </RadioGroup.Items>
      </FormControl.RadioGroup>
      {opensType === 'schedule' && (
        <Controller
          control={form.control}
          name="opensAt"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormControl.Label htmlFor={`opens-at-${id}`}>
                    <T
                      id="Poll.Form.OpensAt.ScheduleLabel"
                      defaultMessage="Voting begins at"
                    />
                  </FormControl.Label>
                </div>
                <DateTimePicker
                  id={`opens-at-${id}`}
                  minDate={new Date()}
                  maxDate={maxDate}
                  value={field.value}
                  onChange={field.onChange}
                />
              </div>
            )
          }}
        />
      )}
    </div>
  )
}
